
import { FiAlertTriangle } from 'react-icons/fi';
import { HiArrowLeft } from 'react-icons/hi';
import { useNavigate } from 'react-router';
import { CrossfadeText } from '../../../../components/common/Animated';
import { DashboardStatus, getDashboardStatusColor, getDashboardStatusLabel } from '../../../../constants/integrationTypes';
import Resources from '../../../../pages/Projects/sections/Project/sections/ProjectBoxComponent/Resources';
import * as def from "../../StrategyMapView.def";
import "./StrategyMapViewSubHeader.css";
import { StrategyMapViewSubHeaderSkeleton } from './StrategyMapViewSubHeaderSkeleton';
import { EntityToolTip } from '../../../EntityToolTip/EntityToolTip';
import RiskStatusDropdown from '../../../../components/common/RiskStatusDropdown';
import { getRiskDetailsFromStats } from '../../../../helpers/riskStatusHelper';
import { getChildEntityBreakdown } from '../../../../utils/entityDisplayUtils';
import DashboardStatusBadge from '../../../../components/common/DashboardStatusBadge';
import { ViewTitle } from '../ViewTitle/ViewTitle';

// Interface for props to support dynamic content in the future
interface StrategyMapViewSubHeaderProps {
    title?: string;
    progress?: number;
    status?: string;
    totalTasks?: number;
    cost?: number;
    duration?: string;
}

/**
 * StrategyMapViewSubHeader - A secondary navigation bar for the Strategy Map View.
 * Mirrors the design of TopNavBar with specific metrics and resource summaries.
 */
export function StrategyMapViewSubHeader({
   state
}: {state: def.StrategyMapViewStateProps}) {

    const navigate = useNavigate();
    
    if (!state?.get?.strageyMapData || state?.get?.loadingSingleStrategyMap) return <StrategyMapViewSubHeaderSkeleton/>
    
    const displayStatus = (() => {
      if (state.get.simulation) return state.get.strageyMapData?.status ?? "";
      return state.get.strageyMapData?.status ?? "";
    })();
    const getCost = () => {
      if (state.get.simulation) return state.get.strageyMapData?.sim_data?.costs?.total_cost ?? 0;
      return state.get.strageyMapData?.costs?.total_cost ?? 0;
    }


      return (
        <div className="smv-sub-header relative">
          {/* Left section: Navigation, Status and Title */}
          <div className="smv-sub-header-left">
            <button
              className="smv-sub-header-back-btn"
              title="Go Back"
              onClick={() => navigate("/strategymaps")}
            >
              <HiArrowLeft className="text-xl text-light-text-secondary dark:text-gray-300" />
            </button>

            <div className="smv-sub-header-title-container">
              {displayStatus == DashboardStatus.AT_RISK ? (
                <RiskStatusDropdown
                  entityName={
                    state.get.strageyMapData.name || "Untitled Project"
                  }
                  entityType="work_unit"
                  entityId={state.get.strageyMapData.id}
                  riskDetails={getRiskDetailsFromStats(
                    state.get.strageyMapData.stats,
                    getChildEntityBreakdown(
                      state.get.strageyMapData.child_entity_types,
                    )[0]?.type || "work item",
                  )}
                  status={state.get.strageyMapData.status}
                >
                  <DashboardStatusBadge
                    status={state.get.strageyMapData.status ?? undefined}
                    withRiskHighlight={true}
                    badgeClassName="text-[10px] px-2 py-1"
                  />
                </RiskStatusDropdown>
              ) : (
                <span
                  className={`relative inline-flex items-center justify-center px-2 py-1 rounded-md text-white text-xs flex-shrink-0 whitespace-nowrap ${getDashboardStatusColor(displayStatus)} shadow-sm transition-all duration-300`}
                >
                  {displayStatus === DashboardStatus.AT_RISK && (
                    <FiAlertTriangle className="mr-1.5 text-white/90" />
                  )}
                  <CrossfadeText
                    text={getDashboardStatusLabel(displayStatus)}
                  />
                </span>
              )}
              <ViewTitle
                name={state?.get?.strageyMapData?.name}
                entityType={"strategy_map"}
                integration_data={state?.get?.strageyMapData?.integration_data}
                type={state?.get?.strageyMapData?.type}
                createdAt={state?.get?.strageyMapData?.created_at}
                createdBy={state?.get?.strageyMapData?.created_by}
                id={state?.get?.strageyMapData?.id}
              />
            </div>
          </div>

          {/* Right section: Key Performance Indicators (KPIs) and Metrics */}
          <div className="smv-sub-header-right items-center">
            {/* Overall project progress visualization */}
            <div className="smv-sub-header-item smv-sub-header-stat">
              <div className="smv-sub-header-stat-header">
                <span className="smv-sub-header-stat-label">
                  Overall Progress
                </span>
                <span className="smv-sub-header-stat-value">
                  {state?.get?.strageyMapData?.progress}%
                </span>
              </div>
              <div className="smv-sub-header-progress-container">
                <div
                  className="smv-sub-header-progress-bar"
                  style={{ width: `${state?.get?.strageyMapData?.progress}%` }}
                />
              </div>
            </div>

            {/* Financial overview */}

            {state.get.showCost && (
              <div className="smv-sub-header-item smv-sub-header-stat max-w-[120px] min-w-[60px]">
                <span className="smv-sub-header-stat-label">Cost</span>
                <span className="smv-sub-header-stat-value">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 0,
                  }).format(getCost())}
                </span>
              </div>
            )}

            {/* Timeline information */}
            {/* <div className="smv-sub-header-stat">
              <span className="smv-sub-header-stat-label">Total Duration</span>
              <span className="smv-sub-header-stat-value">
                {state.get.strageyMapData}
              </span>
            </div> */}

            {/* Composition/Content counts */}
            <div className="smv-sub-header-stat smv-sub-header-item">
              <span className="smv-sub-header-stat-label">Total Items</span>
              <span className="smv-sub-header-stat-value">
                <EntityToolTip data={state.get.strageyMapData}></EntityToolTip>
                {/* {state?.get?.strageyMapData?.entities?.length} */}
              </span>
            </div>

            {/* Team involvement and resource allocation avatars */}
            <div className="smv-sub-header-item smv-sub-header-members">
              <span className="smv-sub-header-members-label">Members</span>
              <div className="flex">
                <Resources
                  resources={state.get.strageyMapData?.members ?? []}
                  projectId={""}
                />
              </div>
            </div>

            {/* <div className="smv-sub-header-skills">
              <div className="smv-sub-header-stat-header">
                <span className="smv-sub-header-stat-label">Skills</span>
                <HiPencilAlt className="text-light-text-secondary dark:text-gray-400 ml-1 cursor-pointer hover:text-light-text-primary transition-colors" />
              </div>
              <div className="flex gap-1.5 mt-1">
                <span className="smv-sub-header-skill-badge bg-blue-500 text-white">
                  React
                </span>
                <span className="smv-sub-header-skill-badge bg-light-surfaceSecondary dark:bg-tertiary-dark border border-light-border-subtle dark:border-gray-600 text-light-text-primary dark:text-gray-300">
                  +2
                </span>
              </div>
            </div> */}
          </div>
        </div>
      );
}

export default StrategyMapViewSubHeader;